import _ from "lodash";
import React from "react";
import moment from "moment";
import {
  getShowAssessmentTool,
  getIsIBGradeScaleTool,
  getDynamicAssessmentTypeOptions,
} from "ClassRoom/modules/utils";
import { getScoreDetailsMemoize } from "ScoreCategories/modules/ScoreCategoriesModule";
import { ASSESSMENT_TOOLS } from "@toddle-edu/common-utils";
import ACLStore from "lib/aclStore";
import {
  TableOutlined,
  SinglePointRubricOutlined,
  RubricOutlined,
  ChecklistOutlined,
  CommentCircleRightOutlined,
  SortOutlined,
  ScoreBasedAssessmentOutlined,
  GoalsOutlined,
  ConfigureColumnsOutlined,
  HamburgerOutlined,
  BadgeStarOutlined,
} from "@toddle-edu/ds-icons";
import {
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_UBD,
  ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY,
  ASSESSMENT_GRADE_BOUNDARY,
  ASSESSMENT_TYPE_QUICK_TASK,
  LEARNING_ENGAGEMENT,
  ASSESSMENT,
  GROUP_TYPE_WORKSHEET,
  GRADE_SCALE_TOOL,
} from "Constants/stringConstants";

import { EXCLUDED_ASSESSMENT_TYPES } from "store/static";
import { ENABLE_ASSESSMENTS_IN_PYP } from "Constants/permissionConstants";
import { ASSESSMENT_TYPE_WORKSHEET } from "Worksheet/modules/WorksheetConstants";

const evaluationTypesWithMYPBasedRubricArr = [
  "ALL",
  "CRITERIA",
  "MYP_BASED_RUBRIC",
];

const mypBasedRubricTypesArr = [
  "MYP_OBJECTIVE_RUBRIC",
  "MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC",
];

const _getEvaluationTypeBasedSubHeading = ({
  evaluationTypes,
  createdAt,
  scoreCategoryLabel,
  isScoreEnabledFromAdmin,
}) => {
  const date = moment(createdAt).format("DD MMM, YYYY");
  switch (evaluationTypes) {
    case "ALL": {
      const subHeading = [date];
      if (isScoreEnabledFromAdmin && scoreCategoryLabel) {
        subHeading.push(scoreCategoryLabel);
      }
      return _.join(subHeading, " • ");
    }
    case "RUBRIC":
    case "comment":
    case "score":
    case "CRITERIA":
    case "CHECKLIST":
    case "MYP_BASED_RUBRIC":
    case "SINGLE_POINT_RUBRIC":
      return `${date}`;
  }
};

export const getEvaluationTypeBasedSubHeading = _.memoize(
  params => _getEvaluationTypeBasedSubHeading(params),
  params => JSON.stringify(params)
);

export const getRubricAssessmentTypeBasedText = ({ t, assessmentToolType }) => {
  // for ASSESSMENT_GRADE_BOUNDARY need to show different locale,

  if (getIsIBGradeScaleTool({ assessmentToolType })) {
    return t("common:ib_grade");
  }

  return t(
    _.find(ASSESSMENT_TOOLS, ({ type }) => {
      return type === assessmentToolType;
    })?.locale
  );
};

export const getFilteredAssignmentsMemoize = _.memoize(
  params => getFilteredAssignments(params),
  params => JSON.stringify(params)
);

const getFilteredAssignments = ({
  assignments,
  evaluationTypes,
  curriculumProgramType,
}) => {
  if (!_.includes(evaluationTypesWithMYPBasedRubricArr, evaluationTypes)) {
    return assignments;
  }

  return _.filter(assignments, (assignment, index) => {
    const content = _.get(assignment, "node.content", {});
    const fields = _.get(content, "fields", []);
    const assessmentTools = _.get(content, "assessmentTools", []);

    const hasAssessmentTool = _.some(assessmentTools?.edges, ({ node }) => {
      const assessmentTool = _.get(node, "assessmentToolItem", {});
      const assessmentToolType = _.get(node, "assessmentToolType", "");

      return getShowAssessmentTool({
        assessmentTool,
        assessmentToolType,
        allFields: fields,
      });
    });

    const assignmentContainsMYPRubric = _.some(
      assessmentTools?.edges,
      ({ node }) => {
        const assessmentTool = _.get(node, "assessmentToolType", "");
        return _.includes(mypBasedRubricTypesArr, assessmentTool);
      }
    );

    const { isScoreEnabled, maxScore } = getScoreDetailsMemoize({
      assessmentTools,
      assessmentFields: _.get(content, "allFields", []),
      curriculumProgramType,
    });

    const hasScore = isScoreEnabled && maxScore;

    const isAssignmentForAllEvaluationType =
      evaluationTypes === "ALL" && (hasAssessmentTool || hasScore);

    const isAssignmentContainsMYPBasedRubric =
      assignmentContainsMYPRubric && hasAssessmentTool;

    if (
      isAssignmentContainsMYPBasedRubric ||
      isAssignmentForAllEvaluationType
    ) {
      return true;
    } else {
      return false;
    }
  });
};

export const getEvaluationTypeBasedAssessmentToolTypeFilterMemoize = _.memoize(
  params => getEvaluationTypeBasedAssessmentToolTypeFilter(params),
  params => JSON.stringify(params)
);

const getEvaluationTypeBasedAssessmentToolTypeFilter = ({
  selectedAssessmentTools = [],
}) => {
  if (!_.isEmpty(selectedAssessmentTools)) {
    const tools = [];

    _.forEach(selectedAssessmentTools, tool => {
      if (_.includes(["MYP_BASED_RUBRIC", "CRITERIA"], tool)) {
        tools.push(
          "MYP_OBJECTIVE_RUBRIC",
          "MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC"
        );
      } else if (_.includes([ASSESSMENT_GRADE_BOUNDARY], tool)) {
        tools.push(
          ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY,
          ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY,
          ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY,
          ASSESSMENT_GRADE_BOUNDARY
        );
      } else {
        tools.push(tool);
      }
    });

    return _.uniq(tools);
  }

  return [];
};

export const getAssessmentToolIcon = ({
  assessmentToolType,
  size = "xxx-small",
  variant = "subtle",
  disabled = false,
}) => {
  const isAssessmentGradebookDataGridEnabled = ACLStore.can(
    "FeatureFlag:EnableAssessmentGradebookDataGrid"
  );

  switch (assessmentToolType) {
    case "RUBRIC":
      return (
        <RubricOutlined variant={variant} size={size} disabled={disabled} />
      );
    case "SINGLE_POINT_RUBRIC":
      if (isAssessmentGradebookDataGridEnabled) {
        return (
          <ConfigureColumnsOutlined
            variant={variant}
            size={size}
            disabled={disabled}
          />
        );
      }
      return (
        <SinglePointRubricOutlined
          variant={variant}
          size={size}
          disabled={disabled}
        />
      );
    case "CHECKLIST":
      if (isAssessmentGradebookDataGridEnabled) {
        return (
          <HamburgerOutlined
            variant={variant}
            size={size}
            disabled={disabled}
          />
        );
      }
      return (
        <ChecklistOutlined variant={variant} size={size} disabled={disabled} />
      );
    case "COMMENT":
      return (
        <CommentCircleRightOutlined
          variant={variant}
          size={size}
          disabled={disabled}
        />
      );
    case ASSESSMENT_GRADE_BOUNDARY:
    case ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY:
    case ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY:
    case ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY:
      return <SortOutlined variant={variant} size={size} disabled={disabled} />;
    case "SCORE":
      return (
        <ScoreBasedAssessmentOutlined
          variant={variant}
          size={size}
          disabled={disabled}
        />
      );
    case "LEARNING_GOALS":
      return (
        <GoalsOutlined variant={variant} size={size} disabled={disabled} />
      );
    case GRADE_SCALE_TOOL:
      return (
        <BadgeStarOutlined variant={variant} size={size} disabled={disabled} />
      );
    default:
      return (
        <TableOutlined variant={variant} size={size} disabled={disabled} />
      );
  }
};

const _getAssesmentTypeOptions = ({ t, evaluationTypes, curriculumType }) => {
  const isCurriculumPYP = _.isEqual(curriculumType, CURRICULUM_TYPE_PYP);
  const isCurriculumUBD = _.isEqual(curriculumType, CURRICULUM_TYPE_UBD);
  const isCurriculumDP = _.isEqual(curriculumType, CURRICULUM_TYPE_DP);

  const QUICK_TASK_TYPE = !isCurriculumDP
    ? [{ label: t("common:quick_task"), key: "qt", value: "qt" }]
    : [];

  const conditionalDropdownFields = _.includes(
    ["CRITERIA", "MYP_BASED_RUBRIC"],
    evaluationTypes
  )
    ? []
    : [
        ...QUICK_TASK_TYPE,
        { label: t("common:le_label"), key: "le", value: "le" },
      ];

  const ASSESSMENT_TYPE = isCurriculumUBD
    ? [
        {
          label: t("common:assessment"),
          key: "assessment",
          value: "assessment",
        },
      ]
    : [];

  const FMT_SMT_TYPES =
    (!ACLStore.can(ENABLE_ASSESSMENTS_IN_PYP) && isCurriculumPYP) ||
    isCurriculumUBD
      ? []
      : [
          {
            label: t("common:assessment_with_type", {
              label: t("common:fmt_label"),
            }),
            key: "fmt",
            value: "fmt",
          },
          {
            label: t("common:assessment_with_type", {
              label: t("common:smt_label"),
            }),
            key: "smt",
            value: "smt",
          },
        ];

  const WORKSHEET_TYPES = ACLStore.can("FeatureFlag:ClassroomWorksheet")
    ? [
        {
          label: t("common:worksheet"),
          key: "worksheet",
          value: "worksheet",
        },
      ]
    : [];

  return [
    ...FMT_SMT_TYPES,
    ...conditionalDropdownFields,
    ...ASSESSMENT_TYPE,
    ...WORKSHEET_TYPES,
  ];
};

export const getAssesmentTypeOptions = _.memoize(
  params => {
    const { curriculumProgramId } = params;

    if (
      curriculumProgramId &&
      ACLStore.can("FeatureFlag:AssessmentTypeConfiguration")
    ) {
      const dynamicOptions = getDynamicAssessmentTypeOptions({
        curriculumId: curriculumProgramId,
      });

      return _.map(dynamicOptions, option => ({
        label: _.get(option, "label"),
        key: _.get(option, "key"),
        value: _.get(option, "value"),
      }));
    }

    return _getAssesmentTypeOptions(params);
  },
  params => JSON.stringify(params)
);

const getGroupOrAssessmentTypes = ({
  curriculumType,
  selectedAssessmentTypes,
}) => {
  switch (curriculumType) {
    case CURRICULUM_TYPE_UBD: {
      const groupTypes = _.map(selectedAssessmentTypes, type => {
        switch (type) {
          case "qt":
            return ASSESSMENT_TYPE_QUICK_TASK;
          case "le":
            return LEARNING_ENGAGEMENT;
          case ASSESSMENT_TYPE_WORKSHEET:
            return GROUP_TYPE_WORKSHEET;
          default:
            return ASSESSMENT;
        }
      });
      return {
        assessmentTypes: undefined,
        groupTypes,
      };
    }

    default: {
      return {
        assessmentTypes: selectedAssessmentTypes,
        groupTypes: undefined,
      };
    }
  }
};

export const getOrgProgressSummaryVariables = params => {
  const { courseId, filters = {}, organizationId, curriculumType } = params;

  const evaluationTypes = _.get(filters, "evaluationTypes", "ALL");
  const selectedAssessmentTools = _.get(filters, "selectedAssessmentTools", []);
  const selectedAssessmentTypes = _.get(filters, "selectedAssessmentTypes", []);
  const selectedTerms = _.get(filters, "selectedTerms", []);
  const searchText = _.get(filters, "searchText", "");
  const selectedCategories = _.get(filters, "selectedCategories", []);
  const selectedSubject = _.get(filters, "subjects", "");

  const isAssessmentScoringEnabled =
    evaluationTypes === "ALL" || evaluationTypes === "score";

  const { assessmentTypes = [], groupTypes = [] } = getGroupOrAssessmentTypes({
    curriculumType,
    selectedAssessmentTypes,
  });

  return {
    organizationId,
    courseIds: courseId,
    contentTypes: "ASSESSMENT",
    isTeacherEvaluationEnabled: true,
    assessmentTypes,
    assessmentToolTypes: getEvaluationTypeBasedAssessmentToolTypeFilterMemoize({
      selectedAssessmentTools,
    }),
    isAssessmentScoringEnabled,
    searchText,
    first: 10,
    academicTermIds: curriculumType === CURRICULUM_TYPE_DP ? selectedTerms : [], // terms filter is there for DP only
    categoryIds: selectedCategories,
    subjectIds:
      !_.isEmpty(selectedSubject) && curriculumType === CURRICULUM_TYPE_UBD // subject filter is there for UBD only
        ? [selectedSubject]
        : [],
    excludedAssessmentTypes:
      curriculumType == "IB_DP" ? EXCLUDED_ASSESSMENT_TYPES : [],
    groupTypes,
    courseMapFilters: {
      courseIds: courseId,
    },
  };
};
